import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { ClientConfiguration } from "../../models/ClientConfiguration/ClientConfiguration";
import LoggingVerbositySelect from "./LoggingVerbositySelect";
import RuntimeScheduleControl from "./RuntimeScheduleControl";
import ServiceVersionSelect from "./ServiceVersionSelect";
import { ImplementationType } from "../../models/OrganizationConfiguration/ImplementationType";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import FieldError from "../common/FieldError";

interface ClientSettingsSectionProps {
  implementationType: ImplementationType;
  disabled?: boolean;
}

const defaultDbConnectionStrings: { [key in ImplementationType]: string } = {
  [ImplementationType.Needles4]: "DSN=Needles",
  [ImplementationType.Needles5Sybase]: "DSN=Needles",
  [ImplementationType.Needles5SQL]: "Server=;Database=Needles;User id=dba;Password=sql;",
  [ImplementationType.TrialWorks]: "Data Source=localhost;Initial Catalog=TrialWorks;Integrated Security=True;",
  [ImplementationType.AzureBlob]: "",
  [ImplementationType.Filevine]: "",
  [ImplementationType.Dropbox]: "",
  [ImplementationType.DocsToNeos]: "",
};

const ClientSettingsSection: React.FC<ClientSettingsSectionProps> = ({
  implementationType,
  disabled = false
}) => {
  const form = useFormContext<ClientConfiguration>();

  const dbConnectionString = form.watch("dbConnectionString");
  const serviceVersion = form.watch("serviceVersion");
  const sendDebugLogs = form.watch("sendDebugLogs");
  const runtimeSchedules = form.watch("runtimeSchedules");

  useEffect(() => {
    if (!dbConnectionString && implementationType) {
      form.setValue("dbConnectionString", defaultDbConnectionStrings[implementationType]);
    }
  }, [implementationType]);

  const showDbField = ![
    ImplementationType.DocsToNeos,
    ImplementationType.Filevine,
    ImplementationType.Dropbox,
    ImplementationType.AzureBlob,
  ].includes(implementationType);

  return (
    <div className="grid grid-cols-12 gap-4 h-full w-full">
      {showDbField && (
        <div className="col-span-12">
          <div>
            <Label>Database Connection String</Label>
            <Input
              disabled={disabled}
              onChange={(e) => form.setValue("dbConnectionString", e.target.value)}
              value={dbConnectionString || ""}
              required
            />
            <FieldError name="dbConnectionString" />
          </div>
        </div>
      )}

      <div className="col-span-12">
        <div className="grid grid-cols-12 gap-2">
          <div className="col-span-6 min-w-[50%]">
            <ServiceVersionSelect
              value={serviceVersion || ""}
              onChange={(v) => form.setValue("serviceVersion", v)}
              disabled={disabled}
            />
          </div>
          <div className="col-span-6 min-w-[50%]">
            <LoggingVerbositySelect
              value={sendDebugLogs}
              onChange={(v) => form.setValue("sendDebugLogs", v)}
              disabled={disabled}
            />
          </div>
        </div>
      </div>

      <div className="col-span-12">
        <RuntimeScheduleControl
          runtimeSchedules={runtimeSchedules || []}
          onChange={(v) => form.setValue("runtimeSchedules", v)}
          disabled={disabled}
        />
      </div>

    </div>
  );
};

export default ClientSettingsSection;
